import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Icon, TextField, Autocomplete } from '@equinor/eds-core-react'
import styled from 'styled-components'
import { DeviceDto, PlantDto, ProjectDto, AreaDto, ExistingImageDto } from '@/core/models/types'
import { useAtom, useAtomValue } from 'jotai'
import {
    showErrorFormAtom,
    selectedAreaAtom,
    inputValuesAtom,
    showErrorOverviewAtom,
    isOfflineAtom,
} from '@/core/store/globalStore'
import { external_link, help_outline, error_outlined, info_circle } from '@equinor/eds-icons'
import {
    baseFont,
    InputStyledLabel,
    StyledErrorIcon,
    StyledErrorTypography,
    StyledHelpButton,
    StyledHelpIcon,
} from '@/styles/layout'
import Overview from './Overview'
import { missingCoordinates } from './OnboardingStepper'
import { COORDINATES_REGEX, normalizeCoordinates } from '../../hooks/useOnboardingStepper'
import { getTagDetails, MIN_TAG_SEARCH_LENGTH } from '@/api/apiService'
import { useSearchStidTagsService } from '@/core/hooks-and-services/onboardedSensorsService'
import { useDebouncedValue } from '@/hooks'
import PopoverDialog from '@/components/popover/PopoverDialog'

interface FormProps {
    areas: AreaDto[]
    selectedPlant: PlantDto | null
    selectedDevice: DeviceDto | null
    selectedProject: ProjectDto | null
    onSelectionChange: (type: 'plant' | 'device' | 'project' | 'area', changes: any) => void
    onClear: (type: 'plant' | 'device' | 'project' | 'area') => void
    onUpdate: (data: any) => void
    currentSensorData: {
        SuperiorTag: string
        Area: string
        Coordinates: string
        Tag: string
        VendorSerialNumber: string
        ImageFiles: File[]
        ImageDescriptions: string[]
        UserAddedDescription: string
        CoordinatesNotAvailable: boolean
        ExistingImages: ExistingImageDto[]
    }
    sensorData: any[]
}

const Form: React.FC<FormProps> = ({
    areas,
    selectedPlant,
    selectedDevice,
    selectedProject,
    onSelectionChange,
    onClear,
    onUpdate,
    currentSensorData,
    sensorData,
}) => {
    const formValues = currentSensorData || {
        SuperiorTag: '',
        Area: '',
        Coordinates: '',
        Tag: '',
        VendorSerialNumber: '',
        ImageFiles: [],
        ImageDescriptions: [],
        UserAddedDescription: '',
        CoordinatesNotAvailable: false,
        ExistingImages: [],
    }

    const tooltipSuperiorTag = 'You can find the superior tag to be monitored in STID or by physical tag.'
    const tooltipArea =
        'Area will be automatically filled if the superior tag has an area linked to it. You can manually change the area afterwards if needed.'
    const wrongCoordinates = 'Incorrect coordinates. Ensure you have found the correct coordinates in Echo.'

    const [isTooltipSuperiorTagOpen, setTooltipSuperiorTagIsOpen] = useState(false)
    const [isTooltipAreaOpen, setTooltipAreaIsOpen] = useState(false)
    const [, setShowErrorForm] = useAtom(showErrorFormAtom)
    const [showErrorOverview] = useAtom(showErrorOverviewAtom)
    const [inputValues, setInputValues] = useAtom(inputValuesAtom)
    const [selectedArea, setSelectedArea] = useAtom(selectedAreaAtom)
    const isOffline = useAtomValue(isOfflineAtom)
    const superiorTagRef = useRef<HTMLDivElement>(null)
    const areaRef = useRef<HTMLDivElement>(null)

    const [superiorTagSearch, setSuperiorTagSearch] = useState(formValues.SuperiorTag)
    const debouncedSuperiorTag = useDebouncedValue(superiorTagSearch, 300)
    // Offline the STID search cannot run, so the tag is typed in by hand. An empty plant disables the query.
    const { data: stidTags = [], isFetching: isSearchingTags } = useSearchStidTagsService(
        isOffline ? '' : (selectedPlant?.shortName ?? ''),
        debouncedSuperiorTag
    )

    // The current value is kept in the list so a free-typed tag stays selectable while results load.
    const superiorTagOptions = useMemo(() => {
        const tags = stidTags.map((tag) => tag.tagNo)
        const current = formValues.SuperiorTag
        return current && !tags.includes(current) ? [current, ...tags] : tags
    }, [stidTags, formValues.SuperiorTag])

    const handleSuperiorTagChange = (value: string) => {
        setSuperiorTagSearch(value)
        if (value !== formValues.SuperiorTag) {
            onUpdate({ ...formValues, SuperiorTag: value })
        }
    }

    useEffect(() => {
        const area = areas.find((area) => area.areaCode === inputValues.Area) ?? null
        setSelectedArea(area)
    }, [inputValues.Area])

    useEffect(() => {
        if (!debouncedSuperiorTag || isOffline) return
        let cancelled = false
        const fetchTagDetails = async () => {
            const superiorTagDetails = await getTagDetails(selectedPlant?.shortName as string, [debouncedSuperiorTag])
            if (cancelled) return
            if (superiorTagDetails[0]?.locationCode != null && inputValues.Area === '') {
                const superiorTagArea: AreaDto = {
                    areaCode: superiorTagDetails[0].locationCode,
                    plantShortName: selectedPlant?.shortName ?? null,
                    description: null,
                }
                setInputValues((prev) => ({ ...prev, Area: superiorTagArea.areaCode as string }))
            }
        }
        fetchTagDetails()
        return () => {
            cancelled = true
        }
    }, [debouncedSuperiorTag, isOffline])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target
        setShowErrorForm(false)
        const nextValue = id === 'Coordinates' ? normalizeCoordinates(value) : value
        onUpdate({ ...formValues, [id]: nextValue })
    }

    const handleCoordinatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, checked } = e.target
        onUpdate({ ...formValues, [id]: checked })
    }

    const shouldCoordinatesBeMandatory = () => {
        return !formValues.CoordinatesNotAvailable && formValues.Coordinates === ''
    }

    const isCoordinatesEmpty = formValues.Coordinates === ''
    const isCoordinatesValid = COORDINATES_REGEX.test(formValues.Coordinates)
    const coordinatesIsNotValid = showErrorOverview && shouldCoordinatesBeMandatory()

    return (
        <FormContainer>
            <Overview
                sensorData={sensorData}
                selectedPlant={selectedPlant}
                selectedDevice={selectedDevice}
                selectedProject={selectedProject}
                currentSensorData={currentSensorData}
                onUpdate={onUpdate}
                displayAdditionalDescription={false}
                displaySensorData={false}
                displayImageNumber={false}
            />
            <RowStyleWithMany>
                <SuperiorTagAutocomplete
                    id="SuperiorTag"
                    label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <InputStyledLabel>Superior tag</InputStyledLabel>
                            <StyledHelpButton
                                ref={superiorTagRef}
                                variant="ghost_icon"
                                onClick={() => setTooltipSuperiorTagIsOpen(!isTooltipSuperiorTagOpen)}
                                $isActive={isTooltipSuperiorTagOpen}
                            >
                                <StyledHelpIcon data={help_outline} size={24} $isActive={isTooltipSuperiorTagOpen} />
                            </StyledHelpButton>
                        </div>
                    }
                    options={superiorTagOptions}
                    optionLabel={(tag: unknown) => tag as string}
                    // STID already filtered on the search term, so keep every returned tag.
                    optionsFilter={() => true}
                    selectedOptions={formValues.SuperiorTag ? [formValues.SuperiorTag] : []}
                    onInputChange={handleSuperiorTagChange}
                    onOptionsChange={(changes) => handleSuperiorTagChange((changes.selectedItems[0] as string) ?? '')}
                    onClear={() => handleSuperiorTagChange('')}
                    loading={isSearchingTags}
                    noOptionsText={
                        isOffline
                            ? 'You are offline. Type the superior tag manually.'
                            : superiorTagSearch.trim().length < MIN_TAG_SEARCH_LENGTH
                              ? 'Type at least 3 characters to search STID'
                              : 'No matching tags in STID'
                    }
                    autoWidth={true}
                    multiline={true}
                />
                <StyledAutocomplete
                    id="Area"
                    label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <InputStyledLabel>Area</InputStyledLabel>
                            <StyledHelpButton
                                ref={areaRef}
                                variant="ghost_icon"
                                onClick={() => setTooltipAreaIsOpen(!isTooltipAreaOpen)}
                                $isActive={isTooltipAreaOpen}
                            >
                                <StyledHelpIcon data={info_circle} size={24} $isActive={isTooltipAreaOpen} />
                            </StyledHelpButton>
                        </div>
                    }
                    options={areas}
                    optionLabel={(area: unknown) => {
                        const { areaCode, description } = area as AreaDto
                        return areaCode && description ? `${areaCode} - ${description}` : ''
                    }}
                    selectedOptions={selectedArea ? [selectedArea] : []}
                    onOptionsChange={(changes) =>
                        onSelectionChange('area', {
                            selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                        })
                    }
                    onClear={() => onClear('area')}
                    autoWidth={true}
                    multiline={true}
                />
            </RowStyleWithMany>

            <RowStyleWithMany style={{ alignItems: 'end' }}>
                <StyledTextField
                    type="text"
                    id="Coordinates"
                    label={
                        <RowStyle>
                            <InputStyledLabel>Coordinates (Echo)</InputStyledLabel>
                        </RowStyle>
                    }
                    value={formValues.Coordinates}
                    onChange={handleChange}
                    required
                    disabled={formValues.CoordinatesNotAvailable}
                    variant={
                        coordinatesIsNotValid ? 'error' : isCoordinatesEmpty || isCoordinatesValid ? undefined : 'error'
                    }
                    $isError={coordinatesIsNotValid}
                />
                <FormStyledLinkButton
                    color="primary"
                    onClick={() =>
                        window.open(
                            `https://echo.equinor.com/echo3d?instCode=${selectedPlant?.shortName || ''}&tagNo=${formValues?.SuperiorTag || ''}`,
                            '_blank',
                            'noopener,noreferrer'
                        )
                    }
                >
                    <FormStyledLinkLabel>{'Echo'}</FormStyledLinkLabel> <StyledIcon data={external_link} />
                </FormStyledLinkButton>
            </RowStyleWithMany>
            {coordinatesIsNotValid && (
                <RowStyle style={{ alignItems: 'flex-start' }}>
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography>{missingCoordinates}</StyledErrorTypography>
                </RowStyle>
            )}
            {!coordinatesIsNotValid && !isCoordinatesEmpty && !isCoordinatesValid && (
                <RowStyle>
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography variant="body_short">{wrongCoordinates}</StyledErrorTypography>
                </RowStyle>
            )}
            <RowStyleWithMany style={{ justifyContent: 'left', alignItems: 'center' }}>
                <RowStyle>
                    <StyledCheckbox
                        id="CoordinatesNotAvailable"
                        onChange={handleCoordinatesChange}
                        checked={formValues.CoordinatesNotAvailable}
                    />
                </RowStyle>
                <RowStyle>
                    <InputStyledLabel>Sensor coordinates do not exist in Echo</InputStyledLabel>
                </RowStyle>
            </RowStyleWithMany>
            <PopoverDialog
                open={isTooltipAreaOpen}
                anchorEl={areaRef.current}
                onClose={() => setTooltipAreaIsOpen(false)}
                title={'Area'}
                content={tooltipArea}
            />
            <PopoverDialog
                open={isTooltipSuperiorTagOpen}
                anchorEl={superiorTagRef.current}
                onClose={() => setTooltipSuperiorTagIsOpen(false)}
                title={'Superior tag'}
                content={tooltipSuperiorTag}
            />
        </FormContainer>
    )
}

const StyledCheckbox = styled(Checkbox)`
    @media (max-width: 1024px) {
        input:checked + div,
        input:checked ~ div {
            background-color: #585858 !important;
            border-color: #585858 !important;
        }

        svg {
            fill: #585858;
        }
    }
`

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-left: 16px;
    margin-right: 16px;
`

const RowStyle = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
`

const RowStyleWithMany = styled(RowStyle)`
    margin-bottom: 4px;
`

const StyledTextField = styled(TextField)<{ $isError?: boolean }>`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    color: white;
    margin-bottom: 0px;
    @media (max-width: 1024px) {
        --eds_interactive_danger__resting: #c6002d;
        --eds_interactive_danger__hover: #c6002d;
        input {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
        .leTuEP {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
        .kwyFQC {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
    }
`

const FormStyledLinkButton = styled(Button)`
    height: 38px;

    @media (max-width: 1024px) {
        width: 100%;
    }
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    width: 24px;
`

const FormStyledLinkLabel = styled.span`
    ${baseFont}
    align-items: last baseline;
    padding: 1px;
    @media (max-width: 1024px) {
        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`
const StyledAutocomplete = styled(Autocomplete)<{ $isError?: boolean }>`
    flex: 1 1 0;
    min-width: 0;
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    color: white;
    margin-bottom: 0px;

    @media (max-width: 1024px) {
        --eds_interactive_danger__resting: #c6002d;
        --eds_interactive_danger__hover: #c6002d;
        input {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
        .leTuEP {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
        .kwyFQC {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
    }
`

const SuperiorTagAutocomplete = styled(StyledAutocomplete)`
    flex-grow: 2;
`

export default Form
